import { useState, useEffect, useRef } from 'react';

const installationServices = [
  { emoji: '⚡', name: 'Electrical Installations', slug: 'electrical-installations' },
  { emoji: '🔌', name: 'Full & Partial Rewiring', slug: 'rewiring' },
  { emoji: '🔧', name: 'Consumer Unit Upgrades', slug: 'consumer-unit-upgrades' },
  { emoji: '💡', name: 'Lighting Installation', slug: 'lighting-installation' },
  { emoji: '🌿', name: 'LED Lighting Upgrades', slug: 'led-lighting' },
  { emoji: '🌳', name: 'Outdoor & Garden Lighting', slug: 'outdoor-garden-lighting' },
  { emoji: '🔦', name: 'Security Lighting', slug: 'security-lighting' },
  { emoji: '🔲', name: 'Socket & Switch Installation', slug: 'socket-switch' },
  { emoji: '🚗', name: 'EV Charger Installation', slug: 'ev-charger' },
  { emoji: '🏠', name: 'Smart Home Installations', slug: 'smart-home' },
  { emoji: '🤖', name: 'Home Automation Systems', slug: 'home-automation' },
  { emoji: '🌡️', name: 'Heating Controls & Thermostats', slug: 'heating-controls' },
  { emoji: '🚿', name: 'Electric Shower Installation', slug: 'electric-shower' },
  { emoji: '🍳', name: 'Kitchen Electrical Installations', slug: 'kitchen-electrical' },
];

const repairServices = [
  { emoji: '🔧', name: 'Electrical Repairs', slug: 'electrical-repairs' },
  { emoji: '🔍', name: 'Fault Finding & Diagnostics', slug: 'fault-finding' },
  { emoji: '⚠️', name: 'Tripping Circuits', slug: 'tripping-circuits' },
  { emoji: '📦', name: 'Fuse Box Repairs', slug: 'fuse-box-repairs' },
  { emoji: '💡', name: 'Lighting Repairs', slug: 'lighting-repairs' },
  { emoji: '🔌', name: 'Socket & Switch Repairs', slug: 'socket-switch-repairs' },
  { emoji: '🚨', name: 'Emergency Call-Outs', slug: 'emergency-callouts' },
  { emoji: '📋', name: 'Electrical Maintenance Contracts', slug: 'maintenance-contracts' },
  { emoji: '✅', name: 'PAT Testing', slug: 'pat-testing' },
  { emoji: '🔨', name: 'Electrical Appliance Repairs', slug: 'appliance-repairs' },
];

const safetyServices = [
  { emoji: '📋', name: 'EICR Testing', slug: 'eicr-testing' },
  { emoji: '🏠', name: 'Landlord Safety Certificates', slug: 'landlord-safety-certificates' },
  { emoji: '🔍', name: 'Homebuyer Electrical Reports', slug: 'homebuyer-electrical-reports' },
  { emoji: '🚨', name: 'Smoke & Heat Alarm Installation', slug: 'smoke-heat-alarm' },
  { emoji: '🔔', name: 'Fire Alarm Systems', slug: 'fire-alarm-systems' },
  { emoji: '⚡', name: 'Surge Protection', slug: 'surge-protection' },
  { emoji: '🔗', name: 'Earthing & Bonding', slug: 'earthing-bonding' },
  { emoji: '✅', name: 'RCD Testing', slug: 'rcd-testing' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setServicesOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setServicesOpen(false), 150);
  };

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 nav-solid ${scrolled ? 'nav-scrolled' : ''}`}>
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center min-w-0">
              <a href="/" className="cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/259e2fbe55ce965c9eea2645e77c4653.png"
                  alt="Beyond Electrical Services Logo"
                  className="h-10 sm:h-12 w-auto"
                />
              </a>
            </div>
            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              <a href="/" className="text-white hover:text-[#FFD700] transition-colors duration-300 text-sm font-medium whitespace-nowrap cursor-pointer">Home</a>

              {/* Services Dropdown */}
              <div
                ref={dropdownRef}
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <button
                  onClick={() => setServicesOpen(!servicesOpen)}
                  className="text-white hover:text-[#FFD700] transition-colors duration-300 text-sm font-medium whitespace-nowrap cursor-pointer flex items-center gap-1 py-6"
                >
                  Services
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {/* Invisible bridge to prevent gap */}
                {servicesOpen && (
                  <div className="absolute top-full left-0 w-full h-2" />
                )}

                {servicesOpen && (
                  <div
                    className="absolute top-[calc(100%+2px)] left-1/2 -translate-x-1/2 w-80 bg-[#0A1628] border border-[#FFD700]/30 rounded-2xl shadow-2xl shadow-[#FFD700]/20 overflow-hidden"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="p-3 max-h-[80vh] overflow-y-auto custom-scrollbar">
                      {/* Installation & Upgrades Section */}
                      <a
                        href="/services/installations"
                        className="block px-4 py-2.5 text-[#FFD700] hover:bg-[#FFD700]/10 rounded-lg transition-all duration-200 font-semibold text-sm mb-2 cursor-pointer"
                      >
                        ⚙️ Installation &amp; Upgrades — All Services
                      </a>
                      <div className="border-t border-[#FFD700]/20 pt-2 mb-3">
                        {installationServices.map((service) => (
                          <a
                            key={service.slug}
                            href={`/services/installations/${service.slug}`}
                            className="flex items-center gap-2.5 px-4 py-2 text-white hover:text-[#FFD700] hover:bg-[#FFD700]/10 rounded-lg transition-all duration-200 text-sm cursor-pointer group"
                          >
                            <span className="text-base group-hover:scale-110 transition-transform duration-200">{service.emoji}</span>
                            <span className="font-medium">{service.name}</span>
                          </a>
                        ))}
                      </div>

                      {/* Repair & Maintenance Section */}
                      <a
                        href="/services/repairs"
                        className="block px-4 py-2.5 text-[#FFD700] hover:bg-[#FFD700]/10 rounded-lg transition-all duration-200 font-semibold text-sm mb-2 cursor-pointer"
                      >
                        🔧 Repair &amp; Maintenance — All Services
                      </a>
                      <div className="border-t border-[#FFD700]/20 pt-2 mb-3">
                        {repairServices.map((service) => (
                          <a
                            key={service.slug}
                            href={`/services/repairs/${service.slug}`}
                            className="flex items-center gap-2.5 px-4 py-2 text-white hover:text-[#FFD700] hover:bg-[#FFD700]/10 rounded-lg transition-all duration-200 text-sm cursor-pointer group"
                          >
                            <span className="text-base group-hover:scale-110 transition-transform duration-200">{service.emoji}</span>
                            <span className="font-medium">{service.name}</span>
                          </a>
                        ))}
                      </div>

                      {/* Safety & Compliance Section */}
                      <a
                        href="/services/safety-compliance"
                        className="block px-4 py-2.5 text-[#FFD700] hover:bg-[#FFD700]/10 rounded-lg transition-all duration-200 font-semibold text-sm mb-2 cursor-pointer"
                      >
                        🛡️ Safety &amp; Compliance — All Services
                      </a>
                      <div className="border-t border-[#FFD700]/20 pt-2">
                        {safetyServices.map((service) => (
                          <a
                            key={service.slug}
                            href={`/services/safety-compliance/${service.slug}`}
                            className="flex items-center gap-2.5 px-4 py-2 text-white hover:text-[#FFD700] hover:bg-[#FFD700]/10 rounded-lg transition-all duration-200 text-sm cursor-pointer group"
                          >
                            <span className="text-base group-hover:scale-110 transition-transform duration-200">{service.emoji}</span>
                            <span className="font-medium">{service.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <a href="/gallery" className="text-white hover:text-[#FFD700] transition-colors duration-300 text-sm font-medium whitespace-nowrap cursor-pointer">Gallery</a>
              <a href="/contact" className="bg-[#FFD700] text-[#0A1628] px-5 py-2 rounded-full font-semibold hover:bg-[#FFC700] transition-all duration-300 shadow-lg text-sm whitespace-nowrap cursor-pointer">Contact</a>
            </div>
            <button
              className="md:hidden text-white p-2 cursor-pointer"
              aria-label="Menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-[#0A1628] overflow-y-auto">
            <div className="flex flex-col items-center justify-start min-h-full space-y-3 px-6 py-24">
              <a
                href="tel:02031981818"
                className="w-full max-w-xs bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-[#0A1628] px-6 py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 cursor-pointer animate-pulse-glow shadow-2xl shadow-[#FFD700]/50 whitespace-nowrap"
              >
                <i className="ri-phone-fill text-xl"></i>
                020 3198 1818
              </a>

              <div className="w-full max-w-xs space-y-2">
                <a href="/" className="block w-full text-center text-white hover:text-[#FFD700] transition-colors duration-300 text-base font-medium py-3 border-b border-[#FFD700]/20 cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Home</a>

                {/* Mobile Services Accordion */}
                <div className="border-b border-[#FFD700]/20">
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="w-full flex items-center justify-center gap-2 text-white hover:text-[#FFD700] transition-colors duration-300 text-base font-medium py-3 cursor-pointer"
                  >
                    Services
                    <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${mobileServicesOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  {mobileServicesOpen && (
                    <div className="pb-3 space-y-1 bg-[#0A1628]">
                      {/* Installation & Upgrades Section */}
                      <a
                        href="/services/installations"
                        className="block w-full text-center text-[#FFD700] hover:bg-[#FFD700]/10 transition-all duration-200 text-sm font-semibold py-2.5 rounded-lg cursor-pointer"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        ⚙️ Installation &amp; Upgrades — All
                      </a>
                      <div className="border-t border-[#FFD700]/20 pt-2 space-y-0.5 mb-3">
                        {installationServices.map((service) => (
                          <a
                            key={service.slug}
                            href={`/services/installations/${service.slug}`}
                            className="flex items-center gap-2 w-full text-left text-white hover:text-[#FFD700] hover:bg-[#FFD700]/10 transition-all duration-200 text-sm py-2 px-3 rounded-lg cursor-pointer"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <span>{service.emoji}</span>
                            <span>{service.name}</span>
                          </a>
                        ))}
                      </div>

                      {/* Repair & Maintenance Section */}
                      <a
                        href="/services/repairs"
                        className="block w-full text-center text-[#FFD700] hover:bg-[#FFD700]/10 transition-all duration-200 text-sm font-semibold py-2.5 rounded-lg cursor-pointer"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        🔧 Repair &amp; Maintenance — All
                      </a>
                      <div className="border-t border-[#FFD700]/20 pt-2 space-y-0.5 mb-3">
                        {repairServices.map((service) => (
                          <a
                            key={service.slug}
                            href={`/services/repairs/${service.slug}`}
                            className="flex items-center gap-2 w-full text-left text-white hover:text-[#FFD700] hover:bg-[#FFD700]/10 transition-all duration-200 text-sm py-2 px-3 rounded-lg cursor-pointer"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <span>{service.emoji}</span>
                            <span>{service.name}</span>
                          </a>
                        ))}
                      </div>

                      {/* Safety & Compliance Section */}
                      <a
                        href="/services/safety-compliance"
                        className="block w-full text-center text-[#FFD700] hover:bg-[#FFD700]/10 transition-all duration-200 text-sm font-semibold py-2.5 rounded-lg cursor-pointer"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        🛡️ Safety &amp; Compliance — All
                      </a>
                      <div className="border-t border-[#FFD700]/20 pt-2 space-y-0.5">
                        {safetyServices.map((service) => (
                          <a
                            key={service.slug}
                            href={`/services/safety-compliance/${service.slug}`}
                            className="flex items-center gap-2 w-full text-left text-white hover:text-[#FFD700] hover:bg-[#FFD700]/10 transition-all duration-200 text-sm py-2 px-3 rounded-lg cursor-pointer"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <span>{service.emoji}</span>
                            <span>{service.name}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <a href="/gallery" className="block w-full text-center text-white hover:text-[#FFD700] transition-colors duration-300 text-base font-medium py-3 border-b border-[#FFD700]/20 cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Gallery</a>
                <a href="/contact" className="block w-full text-center bg-[#FFD700]/20 border-2 border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-[#0A1628] transition-all duration-300 text-base font-bold py-4 rounded-xl mt-2 cursor-pointer" onClick={() => setMobileMenuOpen(false)}>Contact</a>
              </div>

              <div className="flex items-center gap-2 bg-[#FFD700]/10 border border-[#FFD700] text-[#FFD700] px-5 py-2.5 rounded-full text-sm font-semibold">
                <i className="ri-flashlight-fill"></i>
                24/7 Emergency Service
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .nav-solid {
          background: rgb(10, 22, 40);
          box-shadow: 0 4px 20px rgba(255, 215, 0, 0.15);
        }
        .nav-scrolled {
          background: rgb(10, 22, 40);
          backdrop-filter: blur(12px);
          box-shadow: 0 4px 20px rgba(255, 215, 0, 0.15);
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.5); }
          50% { box-shadow: 0 0 40px rgba(255, 215, 0, 0.8); }
        }
        .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255, 215, 0, 0.1); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255, 215, 0, 0.4); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255, 215, 0, 0.6); }
      `}</style>
    </>
  );
}